import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { RefreshCw, Loader2, Download } from 'lucide-react';
import { SystemOverviewStats } from './SystemOverviewStats';
import { BotPerformanceTable } from './BotPerformanceTable';
import { 
  fetchType2SystemStats, 
  fetchType2BotPerformance,
  fetchAllBotsWinRateAllRanges,
  exportBotsWinRateToCSV
} from '@/services/conditional-skip-stats-service';
import { useIsMobile } from '@/hooks/use-mobile';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';

type TimeRange = 'today' | '7days' | '30days' | 'all';

export const Type2SystemOverview: React.FC = () => {
  const [timeRange, setTimeRange] = useState<TimeRange>('7days');
  const [isExporting, setIsExporting] = useState(false);
  const isMobile = useIsMobile();

  const { data: systemStats, isLoading: statsLoading, refetch: refetchStats } = useQuery({
    queryKey: ['type2-system-stats', timeRange],
    queryFn: () => fetchType2SystemStats(timeRange),
    refetchInterval: 30000,
  });

  const { data: botPerformance, isLoading: performanceLoading, refetch: refetchPerformance } = useQuery({
    queryKey: ['type2-bot-performance', timeRange],
    queryFn: () => fetchType2BotPerformance(timeRange),
    refetchInterval: 30000,
  });

  const handleRefresh = () => {
    refetchStats();
    refetchPerformance();
  };

  const handleExportCSV = async () => {
    try {
      setIsExporting(true);
      toast.info('Đang tải dữ liệu từ tất cả time ranges...');
      
      const data = await fetchAllBotsWinRateAllRanges();
      
      if (data.length === 0) {
        toast.warning('Không có dữ liệu để export');
        return;
      }
      
      exportBotsWinRateToCSV(data);
      toast.success(`Đã export ${data.length} bots thành công!`);
    } catch (error) {
      console.error('Export error:', error);
      toast.error('Lỗi khi export CSV');
    } finally {
      setIsExporting(false);
    }
  };

  const isLoading = statsLoading || performanceLoading;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className={cn("flex items-center", isMobile ? "flex-col items-start gap-3" : "flex-row justify-between")}>
        <div>
          <h2 className="text-2xl font-bold">Type 2 System Overview</h2>
          <p className="text-sm text-muted-foreground mt-1">
            Giám sát hiệu suất tổng thể của hệ thống Flexible Pattern Skip
          </p>
        </div>
        <div className={cn("flex items-center gap-2", isMobile && "w-full")}>
          <Select value={timeRange} onValueChange={(value) => setTimeRange(value as TimeRange)}>
            <SelectTrigger className={isMobile ? "flex-1" : "w-[150px]"}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="today">Hôm nay</SelectItem>
              <SelectItem value="7days">7 ngày qua</SelectItem>
              <SelectItem value="30days">30 ngày qua</SelectItem>
              <SelectItem value="all">Tất cả</SelectItem>
            </SelectContent>
          </Select>
          <Button
            size="sm"
            variant="outline"
            onClick={handleExportCSV}
            disabled={isExporting || isLoading}
            className="min-h-[44px] gap-2"
          >
            {isExporting ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Download className="h-4 w-4" />
            )}
            {!isMobile && 'Export CSV'}
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={handleRefresh}
            disabled={isLoading}
            className="min-h-[44px] min-w-[44px]"
          >
            <RefreshCw className={`h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </div>

      {/* System Stats Cards */}
      {isLoading && !systemStats ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin" />
        </div>
      ) : systemStats ? (
        <SystemOverviewStats stats={systemStats} />
      ) : null}

      {/* Bot Performance Table */}
      <Card>
        <CardHeader>
          <CardTitle>Bot Performance</CardTitle>
          <CardDescription>
            Chi tiết hiệu suất từng bot - Sắp xếp theo tổng số signal
          </CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading && !botPerformance ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-8 w-8 animate-spin" />
            </div>
          ) : (
            <BotPerformanceTable 
              bots={botPerformance || []} 
              timeRange={timeRange}
              onRefresh={handleRefresh}
            />
          )}
        </CardContent>
      </Card>
    </div>
  );
};
